<script setup lang="ts">

import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import SaveAndContinueIcon from "@/assets/editViewIcons/SaveAndContinueIcon.vue"
import PrintIcon from "@/assets/editViewIcons/PrintIcon.vue";
import PrintAsIcon from "@/assets/editViewIcons/PrintAsIcon.vue";
import ReviseIcon from "@/assets/editViewIcons/ReviseIcon.vue";
import UnReviseIcon from "@/assets/editViewIcons/UnReviseIcon.vue";
import TreeViewIcon from "@/assets/editViewIcons/TreeViewIcon.vue";
import RefreshIcon from "@/assets/editViewIcons/RefreshIcon.vue";
import SaveIcon from "@/assets/editViewIcons/SaveIcon.vue";
import DuplicateIcon from "@/assets/editViewIcons/DuplicateIcon.vue";
import SaveDraftIcon from "@/assets/editViewIcons/SaveDraftIcon.vue";
import ApprovalIcon from "@/assets/editViewIcons/ApprovalIcon.vue";
import ListViewIcon from "@/assets/editViewIcons/ListViewIcon.vue";
import ShowHelpMsgsIcon from "@/assets/editViewIcons/ShowHelpMsgsIcon.vue";
import DeleteIcon from "@/assets/editViewIcons/DeleteIcon.vue";
import FirstIcon from "@/assets/editViewIcons/FirstIcon.vue";
import PreviousIcon from "@/assets/editViewIcons/PreviousIcon.vue";
import NextIcon from "@/assets/editViewIcons/NextIcon.vue";
import LastIcon from "@/assets/editViewIcons/LastIcon.vue";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import EditViewToolbarUtils from "@/utils/editView/EditViewToolbarUtils.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import MoreMenuItem from "@/utils/UIConstants/MoreMenuItem.ts";
import {type Component, computed, ref} from "vue";
import MoreMenu from "@/components/inputs/MoreMenu.vue";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import Translator from "@/utils/common/Translator.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {EditViewToolbarProps, ToolbarAction} from "@/utils/presenters/interfaces.ts";
import TooltipListIcon from "@/assets/editViewIcons/TooltipListIcon.vue";
import AskAIIcon from "@/assets/editViewIcons/AskAIIcon.vue";
import ClientCache from "@/utils/login/ClientCache.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import ToolbarScreenAction from "@/components/EditView/widgets/ToolbarScreenAction.vue";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import {Screen} from "quasar";
import RemoveFilterIcon from "@/assets/editViewIcons/RemoveFilterIcon.vue";
import TooltipUtils from "@/utils/common/TooltipUtils.ts";
import GUIActionPlaceholder = namaTypes.GUIActionPlaceholder;

const {isSideBarOpen, showToolbarLabels, isUltra} = storeToRefs(useNamaFlagsStore());
const props = defineProps<EditViewToolbarProps>();

const moreMenuItems = ref<MoreMenuItem[] | undefined>();
const moreMenu = ref<typeof MoreMenu | null>(null);

const emit = defineEmits(['toggleTreeForEditView']);
const editScreenToolbarActions = computed<GUIActionPlaceholder[]>(() => {
  const actions: GUIActionPlaceholder[] = [];
  const presenter = props.presenter;
  if (presenter.fetchRecord()?.currentApprovalCase)
    actions.push(EditViewToolbarUtils.getCurrentApprovalAction())
  if (presenter.layout?.editScreenToolbarActions?.length)
    actions.push(...presenter.layout?.editScreenToolbarActions)
  return actions;
})

const editViewActions: ToolbarAction[] = [
  {
    component: AddRecordIcon,
    businessFunc: BusinessFunction.newRecord,
    checkIfEnabled: (a) => props.presenter.preValidateCreateNewRecord()
  }, {
    component: SaveIcon,
    businessFunc: BusinessFunction.save,
    checkIfEnabled: (a) => props.presenter.preValidateSave()
  }, {
    component: SaveAndContinueIcon,
    businessFunc: BusinessFunction.saveAndContinue,
    checkIfEnabled: (a) => props.presenter.preValidateSaveAndContinue()
  }, {
    component: DuplicateIcon,
    businessFunc: BusinessFunction.duplicate,
    checkIfEnabled: (a) => props.presenter.canDuplicate()
  }, {
    component: SaveDraftIcon,
    businessFunc: BusinessFunction.accept,
    title: 'saveDraft',
    checkIfEnabled: (a) => props.presenter.preValidateSaveDraft()
  }, {
    component: RefreshIcon,
    businessFunc: BusinessFunction.refresh,
    checkIfEnabled: (a) => props.presenter.preValidateRefresh()
  }, {
    component: PrintIcon,
    businessFunc: BusinessFunction.print,
    checkIfEnabled: (a) => props.presenter.preValidatePrint()
  }, {
    component: PrintAsIcon,
    businessFunc: BusinessFunction.printAs,
    checkIfEnabled: (a) => props.presenter.preValidatePrint()
  }, {
    component: ApprovalIcon,
    classProps: ['tw-fill-green-800 dark:tw-fill-green-400'],
    businessFunc: BusinessFunction.approval,
    checkIfEnabled: (a) => props.presenter.preValidateApproval()
  }, {
    component: ReviseIcon,
    businessFunc: BusinessFunction.revise,
    checkIfEnabled: (a) => props.presenter.preValidateRevise(),
    checkIfVisible: (a) => props.presenter.canBeRevised()
  }, {
    component: UnReviseIcon,
    businessFunc: BusinessFunction.unrevise,
    checkIfEnabled: (a) => props.presenter.preValidateUnRevise(),
    checkIfVisible: (a) => props.presenter.canBeUnRevised()
  }, {
    component: ListViewIcon,
    businessFunc: BusinessFunction.listView,
    checkIfEnabled: (a) => props.presenter.preValidateGoToListView()
  }, {
    component: ShowHelpMsgsIcon,
    businessFunc: BusinessFunction.showHelpMsgs
  }, {
    component: TreeViewIcon,
    businessFunc: BusinessFunction.treeView,
    checkIfVisible: (a) => props.presenter.preValidateTreeView() && !props.presenter.isQuickCreator()
  }, {
    component: DeleteIcon,
    businessFunc: BusinessFunction.$delete,
    checkIfEnabled: (a) => props.presenter.preValidateDelete()
  }, {
    component: TooltipListIcon,
    businessFunc: BusinessFunction.tooltips,
    checkIfVisible: (a) => TooltipUtils.preValidateTooltipsList(props.presenter.entityType)
  }, {
    component: FirstIcon,
    classProps: [{'tw-rotate-180': Translator.isLanguageRTL()}, '!tw-size-4'],
    businessFunc: BusinessFunction.first,
    checkIfEnabled: (a) => props.presenter.preValidateGoToFirstRecord(),
    checkIfVisible: (a) => !props.presenter.isQuickCreator()
  }, {
    component: PreviousIcon,
    classProps: [{'tw-rotate-180': Translator.isLanguageRTL()}, '!tw-size-4'],
    businessFunc: BusinessFunction.previous,
    checkIfEnabled: (a) => props.presenter.preValidateGoToPreviousRecord(),
    checkIfVisible: (a) => !props.presenter.isQuickCreator()
  }, {
    component: NextIcon,
    classProps: [{'tw-rotate-180': Translator.isLanguageRTL()}, '!tw-size-4'],
    businessFunc: BusinessFunction.next,
    checkIfEnabled: (a) => props.presenter.preValidateGoToNextRecord(),
    checkIfVisible: (a) => !props.presenter.isQuickCreator()
  }, {
    component: LastIcon,
    classProps: [{'tw-rotate-180': Translator.isLanguageRTL()}, '!tw-size-4'],
    businessFunc: BusinessFunction.last,
    checkIfEnabled: (a) => props.presenter.preValidateGoToLastRecord(),
    checkIfVisible: (a) => !props.presenter.isQuickCreator()
  }, {
    component: RemoveFilterIcon,
    businessFunc: BusinessFunction.removeFilter,
    checkIfEnabled: (a) => props.presenter.isHasCriteria(),
    checkIfVisible: (a) => !props.presenter.isQuickCreator()
  }, {
    component: AskAIIcon,
    businessFunc: BusinessFunction.askAI,
    checkIfVisible: (a) => {
      const settings = ClientCache.getCustomGUISettings();
      const canUseAI = CurrentUserUtils.userCanUseAI();

      return Boolean(
        settings?.tooltipsAndReportsAndRevise?.aiModuleAvailable &&
        canUseAI
      );
  }},
];

async function handleShortcut(functions: string[]): Promise<boolean> {
  let toolbarAction = editViewActions.find(a => functions.includes(a.businessFunc));
  if (toolbarAction && EditViewToolbarUtils.isActionEnabled(toolbarAction)) {
    await EditViewToolbarUtils.onToolBarButton(toolbarAction.businessFunc, props.presenter)
    return true;
  }
  if (functions.includes(BusinessFunction.more)) {
    moreMenuItems.value = EditViewToolbarUtils.getMoreMenuItems(props.presenter);
    moreMenu.value!.showPopup();
    return true;
  }
  let moreItems = EditViewToolbarUtils.getMoreMenuItems(props.presenter);
  let item = moreItems.find(i => i.id && functions.includes(i.id));
  if (item)
    await item.callGUIAction(item, props.presenter?.metadata!);
  return false;
}

function isMoreMenuVisible(): boolean {
  return SecurityUtils.userCan(props.presenter.entityType, CapabilityType.CanViewMoreMenu);
}

const allowedBusinessFunctionsToClearErrors = [
  BusinessFunction.refresh, BusinessFunction.duplicate,
  BusinessFunction.save, BusinessFunction.newRecord,
  BusinessFunction.revise, BusinessFunction.unrevise,
  BusinessFunction.accept, BusinessFunction.$delete
]

function handleToolbarButtonClick(action: ToolbarAction) {
  if (allowedBusinessFunctionsToClearErrors.includes(action.businessFunc))
    EditViewUtils.clearErrors();
  EditViewToolbarUtils.onToolBarButton(action.businessFunc, props.presenter, emit)
}

defineExpose({handleShortcut})
</script>

<template>
  <div class="tw-flex tw-gap-2 tw-items-center" :class="[(isUltra && Screen.lt.sm) || !isUltra?'tw-justify-between':'tw-justify-end']">
    <div class="tw-flex tw-gap-2 tw-flex-nowrap tw-overflow-x-auto"
          :class="{'laptop:tw-flex-nowrap laptop:tw-overflow-x-auto':isSideBarOpen}">
      <template v-for="action in editViewActions" :key="action.businessFunc">
        <template v-if="EditViewToolbarUtils.isActionVisible(action)">
          <a
              class="tw-justify-center hover:tw-bg-[#dbdde1] active:tw-bg-gray-300 tw-gap-1 tw-flex tw-flex-col tw-items-center
            tw-p-2 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer  dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600"
              :class="{'nama-toolbar-disabled':EditViewToolbarUtils.isActionDisabled(action)}"
            :title="GUIUtilities.calcToolbarBtnTitle(action.title ?? action.businessFunc)"
            @click.stop="handleToolbarButtonClick(action)">
            <component :is="action.component" class="tw-size-[22px] ultra:tw-size-5"
                       :class="[{'!tw-fill-[#b8bcc2] dark:!tw-fill-gray-600':EditViewToolbarUtils.isActionDisabled(action)},action.classProps]"/>
            <span
              v-if="showToolbarLabels && !alwaysHideLabels && !isUltra"
              v-text="Translator.translateWithFallback(Translator.translateId( action.businessFunc,'BusinessFunction'),action.businessFunc+'Short')"
              class="tw-text-namaFontColor-darkGray dark:tw-text-namaFontColor-dark tw-text-xs tw-select-none tw-capitalize tw-whitespace-nowrap"
            />
          </a>
        </template>
      </template>
      <template v-for="action in editScreenToolbarActions">
        <ToolbarScreenAction :action="action" :presenter="presenter"/>
      </template>
    </div>
    <span class="tw-flex tw-items-center">
      <template v-if="isMoreMenuVisible">
        <a
          class="tw-justify-center tw-flex tw-flex-col tw-gap-1 tw-items-center hover:tw-bg-gray-300 active:tw-bg-gray-400 tw-p-2 ultra:tw-p-1 ultra:tw-my-1 tw-rounded-lg tw-cursor-pointer dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600"
          :title="GUIUtilities.calcToolbarBtnTitle(BusinessFunction.more)"
          @click.stop="moreMenuItems = EditViewToolbarUtils.getMoreMenuItems(props.presenter);">
          <MoreMenu :metadata="presenter.metadata" :moreMenuItems="moreMenuItems" ref="moreMenu"/>
          <span
            v-if="showToolbarLabels && !alwaysHideLabels && !isUltra"
            v-text="Translator.translateId('more')"
            class="tw-text-namaFontColor-darkGray dark:tw-text-namaFontColor-dark  tw-text-xs tw-select-none"/>
        </a>
      </template>
    </span>
  </div>
</template>

<style scoped>

</style>
